
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { XCircle, UserPlus } from "lucide-react";
import { format } from "date-fns";
import { StudentRequest } from "./hooks/useStudentRequests";
import { ptBR, enUS } from "date-fns/locale";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";

interface StudentRequestDialogProps {
  request: StudentRequest;
  onClose: () => void;
  onReject: () => void;
  onCreateStudent: () => void;
}

export const StudentRequestDialog = ({
  request,
  onClose,
  onReject,
  onCreateStudent
}: StudentRequestDialogProps) => {
  const isRequestPending = request.status === "pending";
  const { t } = useTranslations("students");
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  
  // Choose the appropriate locale for date formatting
  const dateLocale = language === 'pt' ? ptBR : enUS;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-md sm:max-w-md dropdown-opaque">
        <DialogHeader className="relative">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold dark:text-white">{t('studentRequests.requestDetails')}</DialogTitle>
            <Breadcrumb className="hidden sm:flex">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/dashboard")} className="dark:text-gray-300">Dashboard</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href={getPath("/dashboard/requests")} className="dark:text-gray-300">{t('studentRequests.title')}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage className="dark:text-white">{t('studentRequests.details')}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </DialogHeader>
        
        <div className="space-y-4 my-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('table.name')}</p>
              <p className="font-medium dark:text-white">{request.first_name} {request.last_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('studentRequests.requestDate')}</p>
              <p className="font-medium dark:text-white">{format(new Date(request.created_at), 'dd/MM/yyyy', { locale: dateLocale })}</p>
            </div>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('table.email')}</p>
            <p className="font-medium dark:text-white">{request.email}</p>
          </div>
          
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('table.phone')}</p>
            <p className="font-medium dark:text-white">{request.phone || t('studentRequests.notProvided')}</p>
          </div>
          
          {request.goals && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('studentRequests.goals')}</p>
              <p className="font-medium dark:text-white">{request.goals}</p>
            </div>
          )}

          {request.notes && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('studentRequests.notes')}</p>
              <p className="font-medium dark:text-white">{request.notes}</p>
            </div>
          )}

          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400">{t('table.status')}</p>
            <p className="font-medium dark:text-white">
              {request.status === "pending" && t('studentRequests.statusPending')}
              {request.status === "approved" && t('studentRequests.statusApproved')}
              {request.status === "rejected" && t('studentRequests.statusRejected')}
            </p>
          </div>

          {request.processed_at && (
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">{t('studentRequests.processedDate')}</p>
              <p className="font-medium dark:text-white">{format(new Date(request.processed_at), 'dd/MM/yyyy', { locale: dateLocale })}</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
          {isRequestPending ? (
            <>
              <Button 
                onClick={onCreateStudent}
                className="bg-flame text-white hover:bg-flame/90 w-full sm:w-auto"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {t('studentRequests.createStudent')}
              </Button>
              <Button 
                onClick={onReject}
                variant="destructive" 
                className="w-full sm:w-auto"
              >
                <XCircle className="h-4 w-4 mr-2" />
                {t('studentRequests.reject')}
              </Button>
            </>
          ) : (
            <Button 
              onClick={onClose} 
              variant="outline" 
              className="w-full sm:w-auto dark:text-white dark:border-gray-600 dark:hover:bg-gray-700"
            >
              {t('studentRequests.close')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
